// A vanilla-DOM full-screen "Cleaning…" overlay shown during an explicit
// sign-out. It is deliberately NOT a React component: the sign-out tears down
// the SDK session, which flips the session store and unmounts the authenticated
// React tree — a React-rendered overlay could be unmounted mid-teardown. By
// appending straight to <body> the overlay survives every React change until
// the hard reload (`forceCleanReload`) loads a fresh document and discards it.
//
// Two uses:
//   1. During `wipeAndReload`, before the reload navigates — "Cleaning…".
//   2. On the freshly-reloaded sign-in page (detected via the one-shot
//      `?_signedout=` param) — a brief "Cleaning up…" so the transition reads
//      as one continuous action instead of an abrupt jump to sign-in.

const OVERLAY_ID = 'ping-cleaning-overlay';
const STYLE_ID = 'ping-cleaning-overlay-style';
const LABEL_ATTR = 'data-ping-cleaning-label';

function ensureKeyframes(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent =
    '@keyframes ping-cleaning-spin{to{transform:rotate(360deg)}}' +
    '@keyframes ping-cleaning-fade{from{opacity:0}to{opacity:1}}';
  document.head.appendChild(style);
}

/**
 * Show (or update) the full-screen cleaning overlay with a spinner + label.
 * Idempotent — calling again only updates the label.
 */
export function showCleaningOverlay(label = 'Cleaning…'): void {
  if (typeof document === 'undefined' || !document.body) return;

  const existing = document.getElementById(OVERLAY_ID);
  if (existing) {
    const text = existing.querySelector(`[${LABEL_ATTR}]`);
    if (text) text.textContent = label;
    return;
  }

  ensureKeyframes();

  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.setAttribute('role', 'status');
  overlay.setAttribute('aria-live', 'polite');
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'justify-content:center',
    'gap:18px',
    'background:rgba(13,15,20,0.94)',
    'backdrop-filter:blur(10px) saturate(160%)',
    '-webkit-backdrop-filter:blur(10px) saturate(160%)',
    'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Lato,sans-serif',
    'animation:ping-cleaning-fade 140ms ease-out',
  ].join(';');

  const spinner = document.createElement('div');
  spinner.style.cssText = [
    'width:46px',
    'height:46px',
    'border-radius:50%',
    'border:3px solid rgba(255,255,255,0.14)',
    'border-top-color:#537EFF',
    'animation:ping-cleaning-spin 0.8s linear infinite',
  ].join(';');

  const text = document.createElement('div');
  text.setAttribute(LABEL_ATTR, '');
  text.textContent = label;
  text.style.cssText = [
    'color:#F5F6F8',
    'font-size:15px',
    'font-weight:600',
    'letter-spacing:0.01em',
  ].join(';');

  overlay.appendChild(spinner);
  overlay.appendChild(text);
  document.body.appendChild(overlay);
}

/** Remove the cleaning overlay if present. */
export function hideCleaningOverlay(): void {
  if (typeof document === 'undefined') return;
  document.getElementById(OVERLAY_ID)?.remove();
}

/**
 * One-shot post-reload continuation: if the current URL carries the
 * `?_signedout=` marker that `forceCleanReload` adds, show the overlay briefly
 * on the freshly-loaded sign-in page, then strip the marker (so a manual
 * refresh won't re-trigger it) and fade out. Safe to call before React mounts.
 */
export function showPostSignOutCleaningIfMarked(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }
  if (!params.has('_signedout')) return;

  showCleaningOverlay('Signing you out...');

  // Strip the marker so a later manual refresh doesn't show the overlay again.
  params.delete('_signedout');
  const query = params.toString();
  const clean =
    window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
  try {
    window.history.replaceState(null, '', clean);
  } catch {
    /* best-effort — history API unavailable */
  }

  window.setTimeout(hideCleaningOverlay, 650);
}
